import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogHeader,
} from "@components/ui/dialog";
import { Trans, useLingui } from "@lingui/react/macro";
import { ExternalLinkIcon, ShareIcon } from "lucide-react";
import { Button } from "@components/ui/button";
import {
  ItemContent,
  ItemTitle,
  ItemDescription,
  ItemActions,
  Item,
} from "@components/ui/item";
import { Field, FieldLabel } from "@components/ui/field";
import { Checkbox } from "@components/Field";
import { MentionCard } from "@/types/MentionCard";
import { toast } from "sonner";
import { useMemo, useState } from "react";

type ShareMentionProps = {
  shareUrl: MentionCard["share_url"];
};

export default function ShareMentionCard({
  shareUrl: _shareUrl,
}: ShareMentionProps) {
  const { t } = useLingui();

  // Local state
  const [showKpis, setShowKpis] = useState<boolean>(true);

  // Helpers
  const shareUrl = useMemo(() => {
    if (showKpis) {
      return _shareUrl;
    } else {
      return `${_shareUrl}?kpis=false`;
    }
  }, [_shareUrl, showKpis]);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button data-testid="share-mention" variant="outline">
          <ShareIcon />
          <Trans>Dela</Trans>
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="share-modal">
        <DialogHeader>
          <DialogTitle>
            <Trans>Dela omnämnande</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>
              Offentliga länkar kan ses av alla. Kopiera en länk för att dela
              detta omnämnande.
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <Item variant="outline" size="sm">
          <ItemContent>
            <ItemTitle className="text-base">
              <Trans>Offentlig länk för omnämnande</Trans>
            </ItemTitle>
            <Field orientation="horizontal">
              <Checkbox
                checked={showKpis}
                id="show-kpis"
                onChange={(e) => {
                  setShowKpis(e.currentTarget.checked);
                }}
              />
              <FieldLabel htmlFor="show-kpis">
                <ItemDescription>
                  <Trans>Visa PR-värde, sentiment och räckvidd</Trans>
                </ItemDescription>
              </FieldLabel>
            </Field>
          </ItemContent>
          <ItemActions>
            <Button size="icon-sm" variant="ghost" asChild>
              <a
                data-testid="share-mention-link-preview"
                href={shareUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLinkIcon />
              </a>
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(shareUrl);
                  toast.success(t`Länken kopierades`);
                } catch {
                  toast.error(t`Länken kunde inte kopierade`);
                }
              }}
            >
              <Trans>Kopiera</Trans>
            </Button>
          </ItemActions>
        </Item>
      </DialogContent>
    </Dialog>
  );
}
